import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/lib/auth';
import { queryClient } from '@/lib/queryClient';
import { ThemeProvider, useTheme } from '@/lib/theme';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { router } from '@/router';

/**
 * Provider order matters:
 *   ThemeProvider   — owns the `dark` class; nothing below it should flash.
 *   QueryClient     — the auth provider restores the session through the api layer,
 *                     and every screen below needs the cache.
 *   AuthProvider    — must sit above the router because the guards read it.
 *   RouterProvider  — owns navigation from here down.
 *
 * AuthProvider deliberately does not use router hooks: guards do the redirecting,
 * which keeps it usable outside a router (and testable).
 */
export function App() {
  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
            <ThemedToaster />
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}

/** Sonner needs to be told the theme; it renders in its own portal. */
function ThemedToaster() {
  const { resolved } = useTheme();
  return (
    <Toaster
      theme={resolved}
      position="bottom-right"
      richColors
      closeButton
      duration={4000}
      toastOptions={{
        classNames: {
          toast: 'text-sm',
        },
      }}
    />
  );
}

export default App;
